import { FC } from "react";
import { DeepPartial } from "react-hook-form";
import { FieldValues, FormDefinition } from "../DynamicForm";

export type StepName = string | number;

export interface NextStep {
  label?: string;
  name: StepName;
  completed: boolean;
  config: FormDefinition<any>;
  composite?: boolean;
}

export interface FormStepComponent<T extends FieldValues> {
  onPrevious: () => void;
  onNext: (value?: T, stepName?: StepName) => Promise<void> | void;
  numberOfSteps: number;
  currentStepNumber: number;
  step: FormStep<T>;
  value: DeepPartial<T>;
  hasZeroStep: boolean;
  steps: NextStep[];
  onGoBack: (stepName: StepName) => void;

  [key: string]: any;
}

export interface FormStep<T extends FieldValues> {
  name: StepName;
  component?: FC<FormStepComponent<T>>;
  // todo find a way to make this generic so we do not need to import LeadWallStep here
  config: FormDefinition<T>;
  possibleTransitions?: StepName[];
  transition?: StepName | ((value: T, stepName?: StepName) => StepName);
  isResult?: boolean;
  navigateTo?: () => string /*| UrlObject*/;
  label?: string;
  /** Form step is not shown in the steps overview */
  hidden?: boolean;
  /**
   * Whether the step is part of the previous step.
   */
  composite?: boolean;
  hideProgress?: boolean;
}

export default class FormSteps<T extends FieldValues> {
  steps: FormStep<T>[] = [];

  private defaultComponent: FC<any> = () => null;

  setDefaultComponent(defaultComponent: FC<any>) {
    this.defaultComponent = defaultComponent;
  }

  addStep(step: FormStep<T>) {
    if (!this.defaultComponent && !step.component) {
      throw new Error(`Missing component config for step ${step.name}`);
    }

    this.steps.push({ component: this.defaultComponent, ...step });
  }
}
